import { ErrorResponse } from '@lemon/core/model/common'
import { Kirjaus, Kirjausrivi } from './kirjanpito'

export type LlmTestiajoTila = 'kaynnissa' | 'valmis' | 'virhe'

export const LLM_KIRJAUSEHDOTUS_KATEGORIAT = [
  'omalla-rahalla',
  'tilitapahtuma-sisaan-myyntilasku',
  'tilitapahtuma-sisaan-muu',
  'tilitapahtuma-ulos-hyvityslasku',
  'tilitapahtuma-ulos-ei-tositetta',
  'tilitapahtuma-ulos-ostolasku-tai-kuitti',
  'muu',
] as const
export type LlmKirjausehdotusKategoria = (typeof LLM_KIRJAUSEHDOTUS_KATEGORIAT)[number]

export const LLM_KIRJAUSEHDOTUS_DETERMINISTISET_KATEGORIAT: ReadonlyArray<LlmKirjausehdotusKategoria> = ['tilitapahtuma-sisaan-myyntilasku', 'tilitapahtuma-ulos-hyvityslasku']

// Kategoriat joissa rahan suunta on tiedossa (esiluokittelu perustui kirjauksen etumerkkiin): vastatilin
// debet/kredit-puoli johdetaan aina kirjauksen summasta, ei LLM:n ehdottamasta puolesta.
export const LLM_KIRJAUSEHDOTUS_KIINTEA_SUUNTA_KATEGORIAT: ReadonlyArray<LlmKirjausehdotusKategoria> = [
  'tilitapahtuma-sisaan-muu',
  'tilitapahtuma-ulos-ei-tositetta',
  'tilitapahtuma-ulos-ostolasku-tai-kuitti',
]

/**
 * Kapeampi kategoriajoukko: testitapaus, testisetti ja kategoriaprofiili-prompt
 * voivat vain ottaa näitä arvoja. Mirroroi DB-tyyppiä `llm_testitapaus_kategoria`
 * — deterministiset kategoriat eivät kuulu testaus-järjestelmään (ne ratkaistaan
 * tuotannossa ilman LLM:ää).
 */
export type LlmTestitapausKategoria = Exclude<LlmKirjausehdotusKategoria, 'tilitapahtuma-sisaan-myyntilasku' | 'tilitapahtuma-ulos-hyvityslasku'>

// ---------------------------------------------------------------------------
// Versioidut promptit
// ---------------------------------------------------------------------------

/**
 * Yksittäinen tilikartta-alue. `alkaa === loppuu` = yksi tili (sis. alitilit,
 * koska suodatus vertaa tilinumeron 4:ää ensimmäistä numeroa). Molemmat 0–9999,
 * alkaa ≤ loppuu.
 */
export interface TilikarttaRaja {
  alkaa: number
  loppuu: number
}

export interface LlmGlobaaliPromptRow {
  versio: number
  prompt: string
  /** ISO 8601 -merkkijono. */
  paivitetty: string
  paivittajaUid: string | null
}

export interface LlmKategoriaprofiiliPromptRow {
  kategoria: LlmTestitapausKategoria
  versio: number
  /** ISO 8601 -merkkijono. */
  paivitetty: string
  prompt: string
  paivittajaUid: string | null
  /** Tilikartta-alueet, joihin LLM:lle välitettävä tilikartta rajataan. Tyhjä = koko tilikartta. */
  tilikarttaRajat: TilikarttaRaja[]
}

export interface LlmTestausHaeGlobaaliPromptUusin {}
export interface LlmTestausHaeGlobaaliPromptUusinVastaus extends ErrorResponse<'unknown-error'> {
  rivi: LlmGlobaaliPromptRow | null
}

export interface LlmTestausHaeGlobaaliPromptHistoria {}
export interface LlmTestausHaeGlobaaliPromptHistoriaVastaus extends ErrorResponse<'unknown-error'> {
  rivit: LlmGlobaaliPromptRow[]
}

export interface LlmTestausTallennaGlobaaliPrompt {
  prompt: string
}
export interface LlmTestausTallennaGlobaaliPromptVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  versio?: number
}

export interface LlmTestausPalautaGlobaaliPromptVersio {
  versio: number
}
export interface LlmTestausPalautaGlobaaliPromptVersioVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  versio?: number
}

export interface LlmTestausHaeKategoriaprofiilinPromptUusin {
  kategoria: LlmTestitapausKategoria
}
export interface LlmTestausHaeKategoriaprofiilinPromptUusinVastaus extends ErrorResponse<'missing-data' | 'invalid-kategoria' | 'unknown-error'> {
  rivi: LlmKategoriaprofiiliPromptRow | null
}

export interface LlmTestausHaeKategoriaprofiilinPromptHistoria {
  kategoria: LlmTestitapausKategoria
}
export interface LlmTestausHaeKategoriaprofiilinPromptHistoriaVastaus extends ErrorResponse<'missing-data' | 'invalid-kategoria' | 'unknown-error'> {
  rivit: LlmKategoriaprofiiliPromptRow[]
}

export interface LlmTestausTallennaKategoriaprofiilinPrompt {
  kategoria: LlmTestitapausKategoria
  prompt: string
  tilikarttaRajat: TilikarttaRaja[]
}
export interface LlmTestausTallennaKategoriaprofiilinPromptVastaus extends ErrorResponse<'missing-data' | 'invalid-kategoria' | 'invalid-tilikarttaraja' | 'unknown-error'> {
  versio?: number
}

export interface LlmTestausPalautaKategoriaprofiilinPromptVersio {
  kategoria: LlmTestitapausKategoria
  versio: number
}
export interface LlmTestausPalautaKategoriaprofiilinPromptVersioVastaus extends ErrorResponse<'missing-data' | 'invalid-kategoria' | 'not-found' | 'unknown-error'> {
  versio?: number
}

export interface LlmTestausHaeKaikkienKategorioidenPromptitUusin {}
export interface LlmTestausHaeKaikkienKategorioidenPromptitUusinVastaus extends ErrorResponse<'unknown-error'> {
  rivit: LlmKategoriaprofiiliPromptRow[]
}

// ---------------------------------------------------------------------------
// Testitapaus + setit
// ---------------------------------------------------------------------------

/** Yhden testitapauksen oikea vaihtoehto (vaihtoehtoinen hyväksytty vastaus). */
export interface LlmTestitapausOikeaVaihtoehto {
  avain: string
  rivit: Kirjausrivi[]
}

/** Yksittäinen testitapaus = yksi tallennettu kirjaus + lukittu kategoria. Kuuluu yhteen settiin. */
export interface LlmTestitapaus {
  avain: string
  asiakasAvain: string
  asiakasNimi: string
  kirjausAvain: string
  kirjaus: Kirjaus
  kategoria: LlmTestitapausKategoria
  maksutapaNimi?: string
  /** Pää-kirjauksen lisäksi hyväksyttävät vaihtoehtoiset vastaukset. */
  oikeatVaihtoehdot?: LlmTestitapausOikeaVaihtoehto[]
}

/** Kategoria-scopattu kokoelma testitapauksia. */
export interface LlmTestisettiKokoelma {
  avain: string
  kategoria: LlmTestitapausKategoria
  nimi: string
  kuvaus: string | null
  /** ISO 8601 -merkkijono. */
  luotu: string
  luojaUid: string
  testitapausLkm: number
}

export interface LlmTestausEsikatseleKirjauksenKategoria {
  asiakasAvain: string
  kirjausAvain: string
}
export interface LlmTestausEsikatseleKirjauksenKategoriaVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  kategoria?: LlmKirjausehdotusKategoria
  onDeterministinen?: boolean
  /** Settien avaimet joihin kirjaus on jo tallennettu testitapauksena. */
  settiAvaimet?: string[]
}

export interface LlmTestausPoistaTestitapaus {
  avain?: string
  kirjausAvain?: string
  asiakasAvain?: string
}
export interface LlmTestausPoistaTestitapausVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

/** Tallentaa muokatun kirjaus-snapshotin testitapaukselle. Snapshot on irrallaan Firestoren "oikeasta" kirjauksesta. */
export interface LlmTestausPaivitaTestitapausKirjaus {
  avain: string
  kirjaus: Kirjaus
}
export interface LlmTestausPaivitaTestitapausKirjausVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {}

/**
 * Synkronoi (asiakasAvain, kirjausAvain) -kirjauksen testisetti-jäsenyydet annettuun
 * tavoitelistaan. Tyhjä `settiAvaimet` ja olemassaoleva testitapaus → testitapaus poistetaan.
 * Tyhjä `settiAvaimet` ja ei testitapausta → no-op. Muuten luo / poistaa / lisää tarpeen mukaan.
 */
export interface LlmTestausSynkronoiKirjauksenJasenyydet {
  asiakasAvain: string
  kirjausAvain: string
  settiAvaimet: string[]
}
export interface LlmTestausSynkronoiKirjauksenJasenyydetVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'kategoria-deterministinen' | 'kategoria-mismatch' | 'unknown-error'> {
  kategoria?: LlmKirjausehdotusKategoria
}

export interface LlmTestausHaeTestitapaukset {
  kategoria?: LlmTestitapausKategoria
  /** Jos annettu, palauttaa vain tähän settiin kuuluvat testitapaukset. */
  settiAvain?: string
}
export interface LlmTestausHaeTestitapauksetVastaus extends ErrorResponse<'unknown-error'> {
  rivit: LlmTestitapaus[]
}

export interface LlmTestausHaeSetit {
  kategoria?: LlmTestitapausKategoria
}
export interface LlmTestausHaeSetitVastaus extends ErrorResponse<'unknown-error'> {
  setit: LlmTestisettiKokoelma[]
}

export interface LlmTestausLuoSetti {
  kategoria: LlmTestitapausKategoria
  nimi: string
  kuvaus?: string
}
export interface LlmTestausLuoSettiVastaus extends ErrorResponse<'missing-data' | 'invalid-kategoria' | 'unknown-error'> {
  avain?: string
}

export interface LlmTestausPaivitaSetti {
  avain: string
  nimi?: string
  kuvaus?: string | null
}
export interface LlmTestausPaivitaSettiVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

export interface LlmTestausPoistaSetti {
  avain: string
}
export interface LlmTestausPoistaSettiVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

export interface LlmTestausPoistaSettiJasenyys {
  settiAvain: string
  testitapausAvain: string
}
export interface LlmTestausPoistaSettiJasenyysVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

export interface LlmTestausLisaaOikeaVaihtoehto {
  testitapausAvain: string
  rivit: Kirjausrivi[]
}
export interface LlmTestausLisaaOikeaVaihtoehtoVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  avain?: string
}

export interface LlmTestausPaivitaOikeaVaihtoehto {
  avain: string
  rivit: Kirjausrivi[]
}
export interface LlmTestausPaivitaOikeaVaihtoehtoVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

export interface LlmTestausPoistaOikeaVaihtoehto {
  avain: string
}
export interface LlmTestausPoistaOikeaVaihtoehtoVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

// ---------------------------------------------------------------------------
// Testiajot
// ---------------------------------------------------------------------------

export interface LlmTestausAloitaTestiAjo {
  testisettiAvain: string
  /** Jos annettu, ajo skopataan näihin testitapauksiin. Muuten ajetaan koko setti. */
  valitutTestitapausAvaimet?: string[]
  tarjoaja: 'claude' | 'openai'
  malli?: string
}

export interface LlmTestausAloitaTestiAjoVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'no-testitapauksia' | 'no-globaali-prompt' | 'no-kategoria-prompt' | 'unknown-error'> {
  kirjausehdotusAjoAvain?: string
}

export interface LlmTestausHaeKirjausehdotusAjot {
  kategoria?: LlmTestitapausKategoria
  testisettiAvain?: string
}

export interface LlmKirjausehdotusAjoTulosRow {
  avain: string
  aloitettu: string
  paattynyt: string | null
  tila: LlmTestiajoTila
  tarjoaja: string
  malli: string
  kirjauksia: number | null
  onnistuneita: number | null
  tiliTarkkuusProsentti: number | null
  suuntaTarkkuusProsentti: number | null
  summaTarkkuusProsentti: number | null
  alvTarkkuusProsentti: number | null
  taydellisetOsumat: number | null
  yhteensaInputTokenit: number | null
  yhteensaOutputTokenit: number | null
  globaaliSystemPrompt: string | null
  kategoriaSystemPrompt: string | null
  jsonKontrakti: string | null
  kayttajanUid: string
  testisettiAvain: string | null
  /** Käytetty UI:ssa pelkkää testisettiAvainta selkeämmäksi näytöksi. Voi olla null jos setti on poistettu. */
  testisettiNimi: string | null
  kategoria: LlmTestitapausKategoria | null
  globaaliPromptVersio: number | null
  kategoriaPromptVersio: number | null
  /** Ajossa käytetyt tilikartta-alueet (kategoriaprofiilin promptversiolta). Tyhjä = koko tilikartta. */
  tilikarttaRajat: TilikarttaRaja[]
  virheViesti: string | null
}

export interface LlmTestausHaeKirjausehdotusAjotVastaus extends ErrorResponse<'unknown-error'> {
  kirjausehdotusAjot: LlmKirjausehdotusAjoTulosRow[]
}

export interface LlmTestausHaeKirjausehdotukset {
  kirjausehdotusAjoAvain: string
}

export interface LlmKirjausehdotusRow {
  avain: string
  kirjausehdotusAjoAvain: string
  asiakasAvain: string
  asiakasNimi: string
  kirjausAvain: string
  kirjausnumero: string
  kirjausPvm: string
  kirjauksenSumma: number | null
  maksunSaaja: string | null
  asiakkaanLisatieto: string | null
  todellisetTilit: string[]
  todellisetDebet: number[]
  todellisetKredit: number[]
  todellisetAlv: string[]
  ehdotetutTilit: string[]
  ehdotetutDebet: number[]
  ehdotetutKredit: number[]
  ehdotetutAlv: string[]
  osumatLkm: number
  suuntaOsumatLkm: number
  summaOsumatLkm: number
  alvOsumatLkm: number
  ehdotusVirhe: string | null
  confidenceLevel: number | null
  perustelu: string | null
  inputTokenit: number
  outputTokenit: number
  ajettu: string
  kategoria: LlmKirjausehdotusKategoria | null
}

export interface LlmTestausHaeKirjausehdotuksetVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  kirjausehdotukset: LlmKirjausehdotusRow[]
}

export interface LlmTestausHaeKirjausehdotusAjonEdistyminen {
  kirjausehdotusAjoAvain: string
}

export interface LlmTestausHaeKirjausehdotusAjonEdistymisVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  tila?: LlmTestiajoTila
  kirjausehdotustenLkm?: number
  kirjauksia?: number | null
  onnistuneita?: number | null
  tiliTarkkuusProsentti?: number | null
  suuntaTarkkuusProsentti?: number | null
  summaTarkkuusProsentti?: number | null
  alvTarkkuusProsentti?: number | null
  taydellisetOsumat?: number | null
}

export type LlmTestausOp =
  | 'esikatseleKirjauksenKategoria'
  | 'poistaTestisetista'
  | 'paivitaTestitapausKirjaus'
  | 'synkronoiKirjauksenJasenyydet'
  | 'haeTestisetti'
  | 'aloitaTestiAjo'
  | 'haeTestiajot'
  | 'haeTulokset'
  | 'haeTestiajonEdistyminen'
  | 'haeGlobaaliPromptUusin'
  | 'haeGlobaaliPromptHistoria'
  | 'tallennaGlobaaliPrompt'
  | 'palautaGlobaaliPromptVersio'
  | 'haeKategoriaprofiilinPromptUusin'
  | 'haeKategoriaprofiilinPromptHistoria'
  | 'tallennaKategoriaprofiilinPrompt'
  | 'palautaKategoriaprofiilinPromptVersio'
  | 'haeKaikkienKategorioidenPromptitUusin'
  | 'haeSetit'
  | 'luoSetti'
  | 'paivitaSetti'
  | 'poistaSetti'
  | 'poistaSettiJasenyys'
  | 'lisaaOikeaVaihtoehto'
  | 'paivitaOikeaVaihtoehto'
  | 'poistaOikeaVaihtoehto'

export interface LlmTestausPyyntoMap {
  esikatseleKirjauksenKategoria: { req: LlmTestausEsikatseleKirjauksenKategoria; resp: LlmTestausEsikatseleKirjauksenKategoriaVastaus }
  poistaTestisetista: { req: LlmTestausPoistaTestitapaus; resp: LlmTestausPoistaTestitapausVastaus }
  paivitaTestitapausKirjaus: { req: LlmTestausPaivitaTestitapausKirjaus; resp: LlmTestausPaivitaTestitapausKirjausVastaus }
  synkronoiKirjauksenJasenyydet: { req: LlmTestausSynkronoiKirjauksenJasenyydet; resp: LlmTestausSynkronoiKirjauksenJasenyydetVastaus }
  haeTestisetti: { req: LlmTestausHaeTestitapaukset; resp: LlmTestausHaeTestitapauksetVastaus }
  aloitaTestiAjo: { req: LlmTestausAloitaTestiAjo; resp: LlmTestausAloitaTestiAjoVastaus }
  haeTestiajot: { req: LlmTestausHaeKirjausehdotusAjot; resp: LlmTestausHaeKirjausehdotusAjotVastaus }
  haeTulokset: { req: LlmTestausHaeKirjausehdotukset; resp: LlmTestausHaeKirjausehdotuksetVastaus }
  haeTestiajonEdistyminen: { req: LlmTestausHaeKirjausehdotusAjonEdistyminen; resp: LlmTestausHaeKirjausehdotusAjonEdistymisVastaus }
  haeGlobaaliPromptUusin: { req: LlmTestausHaeGlobaaliPromptUusin; resp: LlmTestausHaeGlobaaliPromptUusinVastaus }
  haeGlobaaliPromptHistoria: { req: LlmTestausHaeGlobaaliPromptHistoria; resp: LlmTestausHaeGlobaaliPromptHistoriaVastaus }
  tallennaGlobaaliPrompt: { req: LlmTestausTallennaGlobaaliPrompt; resp: LlmTestausTallennaGlobaaliPromptVastaus }
  palautaGlobaaliPromptVersio: { req: LlmTestausPalautaGlobaaliPromptVersio; resp: LlmTestausPalautaGlobaaliPromptVersioVastaus }
  haeKategoriaprofiilinPromptUusin: { req: LlmTestausHaeKategoriaprofiilinPromptUusin; resp: LlmTestausHaeKategoriaprofiilinPromptUusinVastaus }
  haeKategoriaprofiilinPromptHistoria: { req: LlmTestausHaeKategoriaprofiilinPromptHistoria; resp: LlmTestausHaeKategoriaprofiilinPromptHistoriaVastaus }
  tallennaKategoriaprofiilinPrompt: { req: LlmTestausTallennaKategoriaprofiilinPrompt; resp: LlmTestausTallennaKategoriaprofiilinPromptVastaus }
  palautaKategoriaprofiilinPromptVersio: { req: LlmTestausPalautaKategoriaprofiilinPromptVersio; resp: LlmTestausPalautaKategoriaprofiilinPromptVersioVastaus }
  haeKaikkienKategorioidenPromptitUusin: { req: LlmTestausHaeKaikkienKategorioidenPromptitUusin; resp: LlmTestausHaeKaikkienKategorioidenPromptitUusinVastaus }
  haeSetit: { req: LlmTestausHaeSetit; resp: LlmTestausHaeSetitVastaus }
  luoSetti: { req: LlmTestausLuoSetti; resp: LlmTestausLuoSettiVastaus }
  paivitaSetti: { req: LlmTestausPaivitaSetti; resp: LlmTestausPaivitaSettiVastaus }
  poistaSetti: { req: LlmTestausPoistaSetti; resp: LlmTestausPoistaSettiVastaus }
  poistaSettiJasenyys: { req: LlmTestausPoistaSettiJasenyys; resp: LlmTestausPoistaSettiJasenyysVastaus }
  lisaaOikeaVaihtoehto: { req: LlmTestausLisaaOikeaVaihtoehto; resp: LlmTestausLisaaOikeaVaihtoehtoVastaus }
  paivitaOikeaVaihtoehto: { req: LlmTestausPaivitaOikeaVaihtoehto; resp: LlmTestausPaivitaOikeaVaihtoehtoVastaus }
  poistaOikeaVaihtoehto: { req: LlmTestausPoistaOikeaVaihtoehto; resp: LlmTestausPoistaOikeaVaihtoehtoVastaus }
}

export type LlmTestausPyynto = {
  [K in LlmTestausOp]: { op: K; payload: LlmTestausPyyntoMap[K]['req'] }
}[LlmTestausOp]
